import React from "react";
import { Loader2 } from "lucide-react";

import {
  BROKER_FIELD_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { DashboardLayoutDialogFooter } from "@/views/Dashboard/Dashboard/components/DashboardLayoutDialogFooter";

export const CLEAR_ACCOUNT_TRADES_CONFIRM_PHRASE = "clear data";

export type ClearAccountTradeDataTarget = {
  displayName: string;
};

export interface ClearAccountTradeDataDialogProps {
  target: ClearAccountTradeDataTarget | null;
  confirmText: string;
  onConfirmTextChange: (value: string) => void;
  isClearing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ClearAccountTradeDataDialog({
  target,
  confirmText,
  onConfirmTextChange,
  isClearing,
  onCancel,
  onConfirm,
}: ClearAccountTradeDataDialogProps) {
  const accountName = target?.displayName ?? "";
  const confirmMatches =
    confirmText.trim().toLowerCase() === CLEAR_ACCOUNT_TRADES_CONFIRM_PHRASE;

  const handleOpenChange = (open: boolean) => {
    if (!open && !isClearing) onCancel();
  };

  return (
    <StrategyPineDetailDialog
      open={!!target}
      onOpenChange={handleOpenChange}
      title="Clear trade data"
      showHeader={false}
      showDismissButton={false}
      contentClassName="sm:max-w-md"
      bodyClassName="flex-none overflow-visible"
      footer={
        <DashboardLayoutDialogFooter onCancel={onCancel}>
          <Button
            type="button"
            variant="outline-orange"
            size="sm"
            data-font="primary"
            className="shrink-0"
            disabled={!confirmMatches || isClearing}
            aria-busy={isClearing || undefined}
            onClick={onConfirm}
          >
            {isClearing ? (
              <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
            ) : (
              <span className="normal-case tracking-normal">Clear trade data</span>
            )}
          </Button>
        </DashboardLayoutDialogFooter>
      }
    >
      {target ? (
        <Field className="gap-2">
          <FieldLabel htmlFor="broker-clear-trades-confirm" className={BROKER_ALERT_TITLE_CLASS}>
            Clear All Trade Data
          </FieldLabel>
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
            This permanently deletes trades in Tessera for{" "}
            <span className="font-medium text-[#6b7280]">{accountName}</span>. Credentials,
            custom display names, and other account settings are kept. Your live broker account
            balance and portfolio growth curve may still appear on the dashboard. Those come from
            the broker, not Tessera trade history. Enter &lsquo;clear data&rsquo; to confirm.
          </FieldDescription>
          <Input
            id="broker-clear-trades-confirm"
            value={confirmText}
            onChange={(event) => onConfirmTextChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape" && !isClearing) onCancel();
            }}
            placeholder="Type 'clear data' to confirm"
            autoFocus
            disabled={isClearing}
            autoComplete="off"
            data-font="primary"
          />
        </Field>
      ) : null}
    </StrategyPineDetailDialog>
  );
}
